import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { CreateBox } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import { CreateCapsule } from '@babylonjs/core/Meshes/Builders/capsuleBuilder';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { MultiMaterial } from '@babylonjs/core/Materials/multiMaterial';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import type { Scene } from '@babylonjs/core/scene';

import { as2d } from '../core/canvas2d';
import { PoseTrack } from '../net/PoseInterpolator';
import {
  Hands,
  ToolModes,
  toHand,
  toToolMode,
  type AvatarPoseUpdate,
  type HandValue,
  type PresenceDto,
  type ToolModeValue,
} from '../net/wire';
import { cloneModelTemplate, ModelLibrary } from '../room/ModelLibrary';
import { applyTintedMaterial } from '../room/ModelMaterials';
import { VoiceBands, type VoiceBandsSample } from '../voice/VoiceBands';
import { VisorVisualizer } from './VisorVisualizer';

/**
 * The other people in the room: a head, a floating body, and two hands each, smoothed on
 * arrival.
 *
 * What makes a shared session feel shared is knowing where someone is looking and what they
 * are reaching for, and head plus hands alone carries both — a live two-person session found
 * the gap, though: "a floating head is a bit hard to deal with." The fix is not a humanoid
 * body. WebXR gives a head pose and two wrist poses and nothing else — no elbows, shoulders or
 * spine — so a humanoid rig would have to invent those joints with inverse kinematics and look
 * wrong whenever it guessed badly, which is most of the time at a desk. Instead the body takes
 * its silhouette from EVE (Wall-E): head, body and hands float, detached, with nothing
 * connecting them, so the gap the tracking data cannot fill becomes the intended look rather
 * than something to hide. See `plan/open-items.md`, "A body for remote avatars".
 *
 * The body is never tracked and never sent over the wire — no contract change lives here. Its
 * position follows the head's translation at a fixed vertical offset, and its rotation takes
 * the head's *yaw only* (`yawOnlyRotation`, below): inheriting pitch or roll would tumble the
 * torso forward every time someone looked down at the floor.
 */

const HEAD_RADIUS = 0.11;
const HAND_RADIUS = 0.045;

/** Hide a hand that has stopped updating; a frozen hand reads as broken, not as still. */
const HAND_STALE_MS = 400;

/**
 * A capsule stretched shoulder-to-shoulder and flattened front-to-back, so its cross-section is
 * an oval rather than a circle. A plain capsule is a body of revolution around its own axis —
 * it would look identical from every yaw angle, which would make deriving its rotation from the
 * head pointless: there would be nothing for that rotation to visibly do.
 */
const BODY_RADIUS_TOP = 0.13;
const BODY_RADIUS_BOTTOM = 0.08;
const BODY_HEIGHT = 0.32;
const BODY_WIDTH_SCALE = 1.3;
const BODY_DEPTH_SCALE = 0.7;

/**
 * Head-centre to body-centre. Deliberately more than HEAD_RADIUS plus half of BODY_HEIGHT, so
 * the body sits visibly clear of the head — the EVE reference is specifically about the parts
 * *not* touching, and a body built flush against the head would read as a neck, the opposite of
 * the intended look.
 */
const BODY_OFFSET_Y = 0.38;

/**
 * A spectator's own head visibility (plan/roadmap.md, Phase 3 — "present, visibly unable to
 * touch anything"). `AbstractMesh.visibility` below 1 makes `Material.needAlphaBlendingForMesh`
 * enable alpha blending on whatever the head's material turns out to be — a plain
 * `StandardMaterial` for the primitive fallback, or every sub-material of the `MultiMaterial`
 * `applyAvatarMaterial` builds for a model-backed head — without this class having to reach
 * into either one by hand. Confirmed by reading `Material.needAlphaBlendingForMesh` directly
 * (`mesh.visibility < 1.0` is its own explicit check), not assumed.
 */
const SPECTATOR_HEAD_VISIBILITY = 0.45;

/**
 * Where an avatar part's model would live, once the owner has authored one in Blender —
 * `public/models/avatars/`, a subfolder of its own rather than a prefix inside the flat
 * `public/models/` the node catalogue uses. An avatar part is not a `NodeTypeDto` row: it has no
 * catalogue category to prefix a filename with (ModelLibrary.ts, CLAUDE.md's
 * `{category}_{key}.glb` convention), and borrowing that convention anyway would only invite a
 * reader to go looking for a catalogue entry that does not exist. A distinct subfolder keeps
 * that distinction visible in the filesystem itself, the same way `EXPECTED_MODEL_KEYS` staying
 * catalogue-only (ModelLibrary.ts) keeps it visible in code.
 */
const AVATAR_MODEL_PATH_BASE = '/models/avatars/';

/**
 * Keys within AVATAR_MODEL_PATH_BASE — one per part `Avatar` below knows how to build, each
 * resolved to `${AVATAR_MODEL_PATH_BASE}${key}.glb` by `AvatarManager.preloadModels`. Both
 * hands share one key rather than a left/right pair: today they are two clones of the same
 * geometry already, told apart only by tracked position and rotation, not by mesh — so a
 * left/right split has nothing to prove against until the owner actually authors an asymmetric
 * pair, at which point this becomes a `hand-left`/`hand-right` pair and `buildHand` picks
 * between them by `side`, a small, localised change.
 *
 * `head.glb`/`body.glb`/`hand.glb` now ship in `public/models/avatars/` — each authored in
 * Blender with two material slots, `Mat.Avatar.Body` and `Mat.Avatar.Tint` (see
 * `applyAvatarMaterial`'s own doc for what that second slot is for). A `.glb` that fails to
 * fetch or parse still 404s exactly as an un-model-backed catalogue type already does, and
 * `Avatar` still falls back to its built-in primitives in that case — see `templateFor` below.
 *
 * The owner is authoring these at true, real-world scale, not the catalogue's uniform 0.3m: a
 * head about 0.22m across, a body about 0.58m tall by 0.34m wide by 0.18m deep, a hand about
 * 0.09m across. Three different sizes for three different parts is exactly why
 * `preloadModels` below passes `{ normalize: false }` for every one of them — see
 * `LoadModelOptions.normalize` (ModelLibrary.ts) for the rest of that reasoning, including why
 * re-centring is skipped along with scaling rather than kept on its own. Worth knowing where
 * these figures do and do not already match today's primitive stand-ins, for whoever checks a
 * newly-arrived .glb against them: the head and hand figures are exact (HEAD_RADIUS * 2 and
 * HAND_RADIUS * 2 respectively, below), and so is the body's width and depth
 * (BODY_RADIUS_TOP * 2 scaled by BODY_WIDTH_SCALE / BODY_DEPTH_SCALE) — but not its height:
 * BODY_HEIGHT is 0.32m, not 0.58m. That gap is not a bug in either number; the primitive body is
 * a deliberately stubby EVE-style stand-in (see the module doc above), not a preview of the real
 * torso's proportions, and nothing here assumes otherwise.
 */
const AVATAR_MODEL_KEYS = {
  head: 'head',
  body: 'body',
  hand: 'hand',
} as const;

/**
 * The exact name of the per-peer accent slot the owner authors into every avatar part in
 * Blender — the visor, the collar ring, and whatever else each model calls out with it. Every
 * other sub-material (`Mat.Avatar.Body`, the authored white) is left exactly as authored; only a
 * slot named precisely this gets recoloured. Matched by name because that is the one thing the
 * .glb, Blender, and this file can all agree on without a second, hand-kept mapping — the same
 * reasoning `ModelLibrary`'s own `{category}_{key}.glb` filename convention already follows.
 */
const TINT_MATERIAL_NAME = 'Mat.Avatar.Tint';

/**
 * Which `AVATAR_MODEL_KEYS` have already logged a missing-Tint-slot warning this session — see
 * `warnMissingTintSlot`. Module-scope, not per-`AvatarManager`: there is exactly one client
 * process per tab, and "once per key" is meant across every peer that ever joins with that
 * part's model, not once per peer.
 */
const warnedMissingTintKeys = new Set<string>();

/** Logs once per `modelKey` (not once per peer) that a model-backed avatar part has no
 *  `Mat.Avatar.Tint` sub-material — an older or hand-edited export. Never thrown: an avatar must
 *  render regardless, just without a per-peer accent or a visor visualisation for that part. */
function warnMissingTintSlot(modelKey: string): void {
  if (warnedMissingTintKeys.has(modelKey)) {
    return;
  }

  warnedMissingTintKeys.add(modelKey);
  console.warn(
    `Avatar model "${modelKey}.glb" has no "${TINT_MATERIAL_NAME}" material slot — every peer ` +
      'using it falls back to a single whole-mesh tint instead of a per-slot accent, and gets no ' +
      'voice visualisation on it.',
  );
}

/**
 * The avatar-specific call into the shared `applyTintedMaterial` (`room/ModelMaterials.ts`) —
 * pins `tintSlotName` to `TINT_MATERIAL_NAME` and routes a missing slot through
 * `warnMissingTintSlot`'s own once-per-key dedupe, which is avatar-specific bookkeeping
 * (`warnedMissingTintKeys` is keyed by avatar model key, not by anything the shared helper knows
 * about) and so stays here rather than moving into the shared function. See
 * `applyTintedMaterial`'s own doc for the full reasoning — the clone-then-recolour shape used to
 * live here alone; `ToolPropRenderer`'s marker (`Mat.Prop.Tint`) is what made it worth pulling
 * out (`plan/open-items.md`, "The whiteboard marker model is authored and unused").
 */
function applyAvatarMaterial(
  scene: Scene,
  mesh: Mesh,
  modelKey: string,
  instanceName: string,
  presenceColor: Color3,
  fallbackMaterial: StandardMaterial,
): StandardMaterial | undefined {
  return applyTintedMaterial(
    scene,
    mesh,
    TINT_MATERIAL_NAME,
    instanceName,
    presenceColor,
    fallbackMaterial,
    () => warnMissingTintSlot(modelKey),
  );
}

// --- a peer's mode, shown on their dominant hand (plan/remote-tool-visibility.md) -------------

/**
 * Which prop asset a peer's dominant hand shows for each mode — Draw a marker, Scan a scanner,
 * Link a linker, Build the ordinary hand (`null`: needs no special case, since Build's prop
 * genuinely is nothing). Mode only, never the variant within it, by design — Draw's four marker
 * widths plus the eraser and Link's twelve style combinations do not cross the room.
 *
 * **A second, avatar-side copy of the mode -> prop decision `ToolPropRenderer.ts` already makes
 * for the *local* first-person prop** (`heldPropFor`/`ModeRegistry.heldProp`,
 * `input/ModeDefinition.ts`) — not imported from there, for two reasons this task's own brief
 * asked to be named loudly rather than left implicit. First, `HeldPropKind` (`'none' |
 * 'pen-or-eraser' | 'scanner'`) has no `'linker'` member yet — `ToolPropRenderer.ts`'s own "Job 3"
 * doc calls this a known, temporary gap, and `possession/`/`input/` are outside this change's own
 * scope to close it. Second, even where it does have a member, `heldPropFor` answers a different
 * question: `'pen-or-eraser'` still needs `ToolMode.selection`/`markerWidth` to pick a concrete
 * model, none of which travels over the wire (deliberately — "mode only, not the variant"), where
 * this table only ever needs "which one static prop represents this mode." **If
 * `ToolPropRenderer`'s own mode -> prop mapping ever changes, this table has to be updated by
 * hand to match — that drift is a real, standing risk of keeping two copies, not an oversight.**
 */
const AVATAR_TOOL_ASSET_BY_MODE: Readonly<Record<ToolModeValue, string | null>> = {
  [ToolModes.Build]: null,
  [ToolModes.Draw]: 'marker',
  [ToolModes.Scan]: 'scanner',
  [ToolModes.Link]: 'linker',
};

/**
 * Paths for the three prop models a remote avatar can show — **duplicated from
 * `ToolPropRenderer.ts`'s own `MARKER_MODEL_PATH`/`SCANNER_MODEL_PATH`/`LINKER_MODEL_PATH`
 * (`/props/marker.glb`, `/props/scanner.glb`, `/props/linker.glb`) rather than imported**: those
 * constants are not exported, and `possession/` is outside this change's own scope to edit. A
 * second copy of a real path that can drift if the files ever move — flagged loudly per this
 * task's own instruction, not an oversight. Keys match `ToolPropRenderer.ts`'s own model keys
 * (`'marker'`/`'scanner'`/`'linker'`) but cannot collide with `AVATAR_MODEL_KEYS`' own `head`/
 * `body`/`hand` regardless — this is a genuinely separate `ModelLibrary` instance from the one
 * `AuthoringController` builds `ToolPropRenderer` with, each with its own key space.
 */
const TOOL_MODEL_PATHS: Readonly<Record<string, string>> = {
  marker: '/props/marker.glb',
  scanner: '/props/scanner.glb',
  linker: '/props/linker.glb',
};

/**
 * The exact name of a prop model's per-instance accent slot — **duplicated from
 * `ToolPropRenderer.ts`'s own private `PROP_TINT_MATERIAL_NAME`** for the identical reason
 * `TOOL_MODEL_PATHS` above is: that constant is not exported, and `possession/` is out of scope
 * here. The three prop models a remote avatar clones are the exact same `.glb` files
 * `ToolPropRenderer` clones locally, authored with the same two material slots
 * (`Mat.Prop.Body`, `Mat.Prop.Tint`) — a second copy of this string that can drift if the asset
 * author ever renames the slot, flagged loudly for the same reason.
 */
const PROP_TINT_MATERIAL_NAME = 'Mat.Prop.Tint';

/** Which `TOOL_MODEL_PATHS` keys have already logged a missing-Tint-slot warning this session —
 *  `warnMissingTintSlot`'s own sibling, kept as a genuinely separate set (not shared with it)
 *  because the two dedupe over disjoint key spaces: avatar-part keys here would never collide
 *  with tool-asset keys, but sharing one `Set` would make that true by accident rather than by
 *  construction. */
const warnedMissingPropTintKeys = new Set<string>();

/** `warnMissingTintSlot`'s own sibling for a tool prop's `Mat.Prop.Tint` slot. Never thrown: a
 *  remote peer's prop must render regardless, just tinted as a whole mesh instead of on its own
 *  accent slot. */
function warnMissingPropTintSlot(assetKey: string): void {
  if (warnedMissingPropTintKeys.has(assetKey)) {
    return;
  }

  warnedMissingPropTintKeys.add(assetKey);
  console.warn(
    `Tool prop model "${assetKey}" has no "${PROP_TINT_MATERIAL_NAME}" material slot — every ` +
      'peer showing it falls back to a single whole-mesh tint instead of a per-peer accent.',
  );
}

/**
 * The procedural fallback for a remote peer's tool prop, while its `.glb` has not resolved (or
 * has none) — a plain box, tinted by the caller exactly like every other fallback material in
 * this file. Deliberately not a per-kind silhouette the way `ToolPropRenderer.ts`'s own
 * `buildPenMesh`/`buildScannerMesh`/`buildLinkerMesh` are: those exist so the *local* first-person
 * prop still reads correctly at close range with nothing authored yet; a remote peer's prop is
 * seen from across a room, where "something is visibly in that hand" already carries the message
 * this feature exists to send, and a shared shape costs nothing worth avoiding it for.
 */
function buildToolFallbackMesh(scene: Scene, name: string): Mesh {
  const mesh = CreateBox(name, { width: 0.03, height: 0.03, depth: 0.12 }, scene);
  mesh.isPickable = false;
  mesh.rotationQuaternion = Quaternion.Identity();

  return mesh;
}

class Avatar {
  readonly root: TransformNode;
  readonly head: Mesh;
  /** Undefined whenever the head is model-backed — see `buildHead`'s own doc for why a
   *  template head never gets this engine-built box alongside it. */
  readonly visor: Mesh | undefined;
  /**
   * `undefined` for a spectator (plan/roadmap.md, Phase 3: "a head only, semi-transparent,
   * with no hands" — present, visibly unable to touch anything). Not built and immediately
   * hidden — never built at all, the same way a model-backed head never gets `buildHead`'s own
   * primitive visor built alongside it: no mesh means no material to leak either, which is the
   * property CLAUDE.md's "That same cascade does not free materials" note asks every per-peer
   * construction here to keep in mind. `body`/`leftHand`/`rightHand` all share this shape.
   */
  readonly body: Mesh | undefined;
  readonly leftHand: Mesh | undefined;
  readonly rightHand: Mesh | undefined;
  readonly namePlate: Mesh;

  /**
   * The prop currently shown in place of this peer's dominant-hand mesh — `undefined` while
   * their mode is Build, or before any `ToolSwitched` has ever been applied to this avatar
   * (`setTool`, below). Never built for a spectator: `this.leftHand`/`this.rightHand` are both
   * `undefined` for one (see their own doc), and `setTool` reads exactly that to no-op rather
   * than build a prop with no hand slot for `AvatarManager.update()`'s own `applyHand` to ever
   * place it at.
   */
  toolMesh: Mesh | undefined;

  /** Which hand `toolMesh` belongs on — meaningless while `toolMesh` is `undefined`, since
   *  Build (the only mode with no prop) still updates this field on every switch even though
   *  nothing currently reads it. Defaults to right, matching `Hands.Default`/`Handedness.ts`'s
   *  own stored default, for the brief window before this avatar's first `setTool` call. */
  toolHand: HandValue = Hands.Right;

  /** The `AVATAR_TOOL_ASSET_BY_MODE` key `toolMesh` was built from, or `null` for none — lets
   *  `setTool` tell "already showing the right prop, nothing to rebuild" apart from a genuine
   *  change, the same purpose `ToolPropRenderer.shownKind` serves for the local prop. */
  private toolAssetKey: string | null = null;

  /**
   * The visor's voice visualisation — present only when the head resolved a real
   * `Mat.Avatar.Tint` sub-material (`applyAvatarMaterial`'s return value). `undefined` for a
   * primitive-fallback head, or a model-backed one whose `.glb` has no slot by that name: either
   * way there is no distinct accent surface to draw a spectrum onto, and an avatar must render
   * regardless (see `applyAvatarMaterial`'s own doc), just without this.
   */
  private readonly visorVisualizer: VisorVisualizer | undefined;
  private readonly voiceBands = new VoiceBands();

  /**
   * The shared per-peer *fallback* material — built once here, in the constructor, and
   * handed into every `buildHead`/`buildBody`/`buildHand` call regardless of whether that
   * part actually resolves a model. Kept as its own field, rather than trusted to turn up on
   * some mesh's `.material` by the time `dispose()` runs, because on the model-backed path —
   * every shipped avatar part today (`AVATAR_MODEL_KEYS`'s own doc) — it never does:
   * `applyAvatarMaterial` assigns the template's own cloned `MultiMaterial` instead, and the
   * fallback argument is only ever read for `warnMissingTintSlot`'s failure branch. Nothing
   * in the per-mesh dispose loop below could ever reach an instance no mesh references, which
   * is exactly the leak this field exists to close — see `dispose()`'s own doc for the other
   * half (a *primitive*-fallback avatar assigns this same instance to up to four meshes, and
   * that direction needed its own fix, not just this one).
   */
  private readonly bodyMaterial: StandardMaterial;

  readonly headTrack = new PoseTrack();
  readonly leftTrack = new PoseTrack();
  readonly rightTrack = new PoseTrack();

  leftPinch = false;
  rightPinch = false;

  /**
   * Sender-clock alignment. Samples carry the sender's own `Date.now()`, which has no fixed
   * relationship to ours, so instead of trying to align the two clocks we advance the last
   * sender timestamp by locally elapsed time. Skew cancels out and only the rate matters.
   */
  senderClockAtLastPacket = 0;
  localClockAtLastPacket = 0;

  constructor(
    private readonly scene: Scene,
    readonly presence: PresenceDto,
    models: ModelLibrary,
  ) {
    const color = Color3.FromHexString(presence.colorHex);

    this.root = new TransformNode(`avatar-${presence.peerId}`, scene);

    const bodyMaterial = new StandardMaterial(`avatar-${presence.peerId}`, scene);
    bodyMaterial.diffuseColor = color;
    bodyMaterial.emissiveColor = color.scale(0.25);

    // Every material in this project defaults specularColor to black (CLAUDE.md: "Specular
    // highlights on flat walls read as smears when the head moves"), and that reasoning is
    // sound for a flat wall and wrong for a curved body — a highlight sweeping across a sphere
    // or capsule as the head moves is exactly what reads as *shape* rather than a flat-shaded
    // silhouette. The primitive visor (buildVisor, below) already breaks the black-specular rule
    // for the same reason and looks better for it; this gives the head, body and hands the same
    // real, if modest, specular response. Not chrome: specularColor stays a dim grey and
    // specularPower broader than the engine default (64) — a soft sheen that traces curvature as
    // someone moves, rather than a small hot point highlight.
    bodyMaterial.specularColor = new Color3(0.3, 0.3, 0.3);
    bodyMaterial.specularPower = 32;

    this.bodyMaterial = bodyMaterial;

    const { head, visor, tintMaterial } = this.buildHead(scene, presence, color, bodyMaterial, models);
    this.head = head;
    this.visor = visor;

    // A spectator gets a head only — no body, no hands, present and visibly unable to touch
    // anything (plan/roadmap.md, Phase 3). See `body`'s own doc for why this skips construction
    // entirely rather than building and hiding.
    if (presence.isSpectator) {
      this.head.visibility = SPECTATOR_HEAD_VISIBILITY;
      this.body = undefined;
      this.leftHand = undefined;
      this.rightHand = undefined;
    } else {
      this.body = this.buildBody(scene, presence, color, bodyMaterial, models);
      this.leftHand = this.buildHand(scene, presence, color, bodyMaterial, 'left', models);
      this.rightHand = this.buildHand(scene, presence, color, bodyMaterial, 'right', models);
    }

    this.namePlate = this.buildNamePlate(scene, presence);
    this.namePlate.parent = this.root;

    // The visor's voice visualisation attaches to the head's own accent material, if it has
    // one — see the field's own doc for why that can legitimately be absent.
    this.visorVisualizer = tintMaterial
      ? new VisorVisualizer(scene, tintMaterial, presence.colorHex, `visor-visualizer-${presence.peerId}`)
      : undefined;
  }

  /**
   * A peer's microphone just went live — `AvatarManager.voiceStreamStarted` forwards
   * `PeerVoicePlayer`'s own `<audio>` element here, unchanged, the same lookup-not-plumbing join
   * the task this exists for calls for: no new hub method, no new wire field, just two things
   * already keyed by the same `peerId`. A no-op when this avatar has no visor visualisation to
   * drive (see `visorVisualizer`'s own doc) — the audio still plays through `PeerVoicePlayer`
   * regardless; only the visor's own reaction to it is skipped.
   */
  startVoice(audio: HTMLAudioElement): void {
    if (!this.visorVisualizer) {
      return;
    }

    this.voiceBands.start(audio, (sample: VoiceBandsSample) => this.visorVisualizer?.draw(sample, true));
  }

  /**
   * The matching close for `startVoice` — also reached from `dispose()` below, so a peer who
   * disconnects mid-stream (no `VoiceStreamEnded` ever arrives for that case — see
   * `PeerVoicePlayer`'s own remarks) still tears its `AudioContext` down rather than leaking it.
   */
  endVoice(): void {
    this.voiceBands.stop();
    this.visorVisualizer?.draw({ bands: [], rms: 0 }, false);
  }

  /**
   * Applies a `ToolSwitched` event to this avatar — swaps the dominant-hand mesh's own tracked
   * pose over to a prop mesh, or back to the ordinary hand for Build (plan/remote-tool-
   * visibility.md). `AvatarManager.update()`'s own `applyHand` is what actually *positions*
   * whichever mesh is current every frame; this method only decides which one that is.
   *
   * A no-op for a spectator (`this.leftHand`/`this.rightHand` both `undefined` — see their own
   * doc): there is no hand slot for a prop to occupy, and main.ts never sends a switch for one
   * in the first place (plan/remote-tool-visibility.md, "Spectators") — this is the defensive
   * second layer, not the primary guard.
   */
  setTool(mode: ToolModeValue, hand: HandValue, models: ModelLibrary): void {
    if (this.leftHand === undefined || this.rightHand === undefined) {
      return;
    }

    this.toolHand = hand;

    const assetKey = AVATAR_TOOL_ASSET_BY_MODE[mode];

    if (assetKey === this.toolAssetKey) {
      // Already showing the right prop (or already showing none, for two consecutive Build
      // switches) — nothing to rebuild. Still worth reaching this far for the `toolHand`
      // assignment above: a hand-only change with the mode unchanged (unusual, but not
      // impossible if handedness flips while a peer is mid-mode) still has to move where the
      // still-current prop renders.
      return;
    }

    this.disposeToolMesh();

    if (assetKey === null) {
      return;
    }

    this.toolMesh = this.buildToolMesh(assetKey, models);
    this.toolAssetKey = assetKey;
  }

  /**
   * The tool prop's mesh: the authored `.glb` once `AvatarManager.preloadModels()` has resolved
   * it under `assetKey` (`TOOL_MODEL_PATHS`), `buildToolFallbackMesh`'s procedural box otherwise
   * — the same template-or-primitive shape `buildHead`/`buildBody`/`buildHand` below already
   * use, and the same clone-then-tint dance `ToolPropRenderer.buildTemplatedProp` uses for the
   * *local* prop, reused here via the shared `applyTintedMaterial` rather than copied a third
   * time. Tinted with this peer's own presence colour — the same colour their head, body and
   * hands already carry — rather than a fixed accent the way the local scanner/linker render:
   * a remote prop's whole reason to exist is "whose hand is this," which a peer's own colour
   * answers and a fixed accent does not.
   */
  private buildToolMesh(assetKey: string, models: ModelLibrary): Mesh {
    const instanceName = `tool-${assetKey}-${this.presence.peerId}`;
    const color = Color3.FromHexString(this.presence.colorHex);

    const fallbackMaterial = new StandardMaterial(`${instanceName}-fallback-material`, this.scene);
    fallbackMaterial.specularColor = Color3.Black();
    fallbackMaterial.diffuseColor = color;
    fallbackMaterial.emissiveColor = color.scale(0.25);

    const template = models.templateFor(assetKey);

    if (!template) {
      const mesh = buildToolFallbackMesh(this.scene, instanceName);
      mesh.material = fallbackMaterial;
      mesh.parent = this.root;
      mesh.setEnabled(false);

      return mesh;
    }

    const mesh = cloneModelTemplate(template, instanceName);
    mesh.isPickable = false;
    mesh.rotationQuaternion = Quaternion.Identity();

    const tintMaterial = applyTintedMaterial(
      this.scene,
      mesh,
      PROP_TINT_MATERIAL_NAME,
      instanceName,
      color,
      fallbackMaterial,
      () => warnMissingPropTintSlot(assetKey),
    );

    if (tintMaterial) {
      // The tint slot resolved, so applyTintedMaterial never assigned fallbackMaterial anywhere
      // — dispose it now rather than leave it unreferenced in the scene for the rest of the
      // session (the identical reasoning ToolPropRenderer.buildTemplatedProp's own doc gives).
      fallbackMaterial.dispose();
    }

    mesh.parent = this.root;
    mesh.setEnabled(false);

    return mesh;
  }

  /**
   * Frees the currently-shown tool prop's mesh and material — called from `setTool` on every
   * genuine change (including back to Build/`null`), and from `dispose()` below for whatever is
   * still shown at avatar teardown. `MultiMaterial.dispose(false, false, true)` for the
   * model-backed branch, matching `ToolPropRenderer.disposeMesh`'s own reasoning exactly: a
   * plain `mesh.dispose()` frees the mesh but leaves every per-instance sub-material this
   * avatar's own `buildToolMesh` cloned (`applyTintedMaterial`) sitting unreferenced in the
   * scene (CLAUDE.md, "That same cascade does not free materials").
   */
  private disposeToolMesh(): void {
    if (!this.toolMesh) {
      return;
    }

    const material = this.toolMesh.material;

    if (material instanceof MultiMaterial) {
      material.dispose(false, false, true);
    } else {
      material?.dispose();
    }

    this.toolMesh.dispose();
    this.toolMesh = undefined;
    this.toolAssetKey = null;
  }

  /**
   * A model-backed part if one has been preloaded, its built-in primitive otherwise — the same
   * `template ? cloneModelTemplate(...) : buildPrimitive(...)` shape `NodeMeshFactory.createShape`
   * already uses for a catalogue node. `head.glb` ships in `public/models/avatars/`
   * (AVATAR_MODEL_PATH_BASE above) now, so this takes the template branch whenever
   * `AvatarManager.preloadModels()` has resolved it — not yet confirmed on hardware; the
   * primitive branch remains the one that is.
   *
   * Also decides whether a visor gets built at all. The owner's authored `head.glb` includes its
   * own visor as part of the model, so a template-backed head builds none here — stacking the
   * engine's own box on top of it would intersect the model's own geometry. A bare primitive
   * sphere has no face otherwise, which reads badly, so the primitive branch keeps building one
   * (buildVisor, below) exactly as before. This is why `visor` is `Mesh | undefined` rather than
   * always present: the model-backed path never creates one, and every caller that touches it
   * (Avatar.dispose, currently the only one) has to treat that as the normal case, not an
   * exception.
   *
   * The template branch no longer assigns `material` (the shared per-peer body material)
   * wholesale — that was the second half of the "avatar renders as one flat colour" bug this
   * change fixes, alongside `ModelLibrary`'s own `preserveMaterials` option: overwriting
   * `head.material` here would have discarded the merged `MultiMaterial` `applyAvatarMaterial`
   * needs to find `Mat.Avatar.Tint` in, one line after building it. The primitive branch keeps
   * assigning `material` exactly as before — it was never merged from a multi-slot model and has
   * no slots to preserve.
   */
  private buildHead(
    scene: Scene,
    presence: PresenceDto,
    color: Color3,
    material: StandardMaterial,
    models: ModelLibrary,
  ): { head: Mesh; visor: Mesh | undefined; tintMaterial: StandardMaterial | undefined } {
    const template = models.templateFor(AVATAR_MODEL_KEYS.head);
    const head = template
      ? cloneModelTemplate(template, `head-${presence.peerId}`)
      : CreateSphere(`head-${presence.peerId}`, { diameter: HEAD_RADIUS * 2, segments: 10 }, scene);

    const tintMaterial = template
      ? applyAvatarMaterial(scene, head, AVATAR_MODEL_KEYS.head, `head-${presence.peerId}`, color, material)
      : undefined;

    if (!template) {
      head.material = material;
    }

    head.isPickable = false;
    head.parent = this.root;

    const visor = template ? undefined : this.buildVisor(scene, presence, head);

    return { head, visor, tintMaterial };
  }

  /**
   * The primitive fallback's dark face plate, so which way someone is looking is obvious at a
   * glance — without it a bare coloured sphere gives no orientation at all. Only ever called from
   * the no-template branch of `buildHead` above; a model-backed head supplies its own visor as
   * part of the .glb, and this is never built alongside it.
   *
   * Owns its own material rather than sharing the head/body/hands' one, so it can stay a
   * near-black plate regardless of the peer's colour — built here, lazily, rather than eagerly in
   * the constructor the way it used to be: constructing it before `buildHead` has decided whether
   * a visor is even wanted would leave an unused `StandardMaterial` sitting in the scene whenever
   * a head template exists, never assigned to any mesh and therefore never reached by
   * `Avatar.dispose`'s per-mesh `mesh?.material?.dispose()` loop either. Building it only on the
   * branch that actually uses it means there is never a material to leak in the first place.
   */
  private buildVisor(scene: Scene, presence: PresenceDto, head: Mesh): Mesh {
    const visorMaterial = new StandardMaterial(`avatar-visor-${presence.peerId}`, scene);
    visorMaterial.diffuseColor = new Color3(0.05, 0.06, 0.08);
    visorMaterial.specularColor = new Color3(0.2, 0.2, 0.2);

    const visor = CreateBox(`visor-${presence.peerId}`, { width: 0.14, height: 0.06, depth: 0.02 }, scene);
    visor.material = visorMaterial;
    visor.isPickable = false;
    visor.parent = head;
    visor.position.set(0, 0.01, HEAD_RADIUS * 0.92);

    return visor;
  }

  /**
   * The floating torso. Position and rotation are not set here — like the head and hands, they
   * are driven fresh every frame in `AvatarManager.update()` from the head's own tracked pose,
   * never parented or otherwise attached to it, so there is no transform relationship for a
   * connecting neck to accidentally fall out of.
   *
   * See `buildHead` above for the template-or-primitive shape this follows, and for why the
   * template branch calls `applyAvatarMaterial` instead of assigning `material` wholesale.
   */
  private buildBody(
    scene: Scene,
    presence: PresenceDto,
    color: Color3,
    material: StandardMaterial,
    models: ModelLibrary,
  ): Mesh {
    const template = models.templateFor(AVATAR_MODEL_KEYS.body);
    const body = template
      ? cloneModelTemplate(template, `body-${presence.peerId}`)
      : CreateCapsule(
          `body-${presence.peerId}`,
          { height: BODY_HEIGHT, radiusTop: BODY_RADIUS_TOP, radiusBottom: BODY_RADIUS_BOTTOM },
          scene,
        );

    if (!template) {
      // Compensates for CreateCapsule's own rotational symmetry about its long axis — a solid
      // of revolution shows no yaw at all (CLAUDE.md, "A body of revolution shows no yaw at
      // all"). An authored model has no reason to be a solid of revolution to begin with, so
      // this correction is scoped to the primitive fallback rather than applied unconditionally
      // — it would only distort a real model's own, presumably already-asymmetric, proportions.
      body.scaling.set(BODY_WIDTH_SCALE, 1, BODY_DEPTH_SCALE);
      body.material = material;
    } else {
      applyAvatarMaterial(scene, body, AVATAR_MODEL_KEYS.body, `body-${presence.peerId}`, color, material);
    }

    body.isPickable = false;
    body.parent = this.root;
    body.setEnabled(false);

    return body;
  }

  /** See `buildHead` above for the template-or-primitive shape this follows, and for why the
   *  template branch calls `applyAvatarMaterial` instead of assigning `material` wholesale. Both
   *  hands share one template key (AVATAR_MODEL_KEYS.hand) — see that constant's own doc for
   *  why — but each side still gets its own instance name, so their per-peer material clones
   *  (and any missing-Tint-slot naming) stay distinguishable from one another. */
  private buildHand(
    scene: Scene,
    presence: PresenceDto,
    color: Color3,
    material: StandardMaterial,
    side: string,
    models: ModelLibrary,
  ): Mesh {
    const template = models.templateFor(AVATAR_MODEL_KEYS.hand);
    const hand = template
      ? cloneModelTemplate(template, `${side}-hand-${presence.peerId}`)
      : CreateSphere(`${side}-hand-${presence.peerId}`, { diameter: HAND_RADIUS * 2, segments: 8 }, scene);

    if (!template) {
      hand.material = material;
    } else {
      applyAvatarMaterial(
        scene,
        hand,
        AVATAR_MODEL_KEYS.hand,
        `${side}-hand-${presence.peerId}`,
        color,
        material,
      );
    }

    hand.isPickable = false;
    hand.parent = this.root;
    hand.setEnabled(false);

    return hand;
  }

  private buildNamePlate(scene: Scene, presence: PresenceDto): Mesh {
    const texture = new DynamicTexture(`nameplate-${presence.peerId}`, { width: 256, height: 64 }, scene, true);
    const context = as2d(texture.getContext());

    context.clearRect(0, 0, 256, 64);
    context.fillStyle = presence.colorHex;
    context.font = 'bold 34px system-ui, sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(presence.displayName.slice(0, 18), 128, 34);
    texture.update();

    const material = new StandardMaterial(`nameplate-${presence.peerId}`, scene);
    material.diffuseTexture = texture;
    material.opacityTexture = texture;
    material.emissiveColor = new Color3(1, 1, 1);
    material.disableLighting = true;
    material.specularColor = Color3.Black();

    const plate = CreatePlane(`nameplate-${presence.peerId}`, { width: 0.4, height: 0.1 }, scene);
    plate.material = material;
    plate.isPickable = false;

    // Always readable regardless of where the person is facing.
    plate.billboardMode = Mesh.BILLBOARDMODE_ALL;

    return plate;
  }

  dispose(): void {
    // Not reached from a matching endVoice() call for every peer — a peer who disconnects
    // mid-stream never gets one (PeerVoicePlayer's own remarks) — so this is the one path
    // guaranteed to run regardless, freeing VoiceBands' own AudioContext and the visor's texture
    // (VisorVisualizer.dispose) rather than leaking either.
    this.voiceBands.stop();
    this.visorVisualizer?.dispose();

    // Whatever tool prop is currently shown (plan/remote-tool-visibility.md) — its own dedicated
    // dispose path, not folded into the generic loop below: `disposeToolMesh` already frees both
    // shapes of material correctly (see its own doc), and a no-op when nothing is currently shown
    // (Build, or an avatar this was never called on at all).
    this.disposeToolMesh();

    // this.visor is undefined whenever the head is model-backed (buildHead) — optional chaining
    // on the element itself, not just its material, is what makes that the normal case here
    // rather than a crash; every other element in this array is always present.
    for (const mesh of [this.head, this.visor, this.body, this.leftHand, this.rightHand, this.namePlate]) {
      const material = mesh?.material;

      if (material instanceof MultiMaterial) {
        // Every sub-material on a per-peer MultiMaterial here is this avatar's own clone
        // (applyAvatarMaterial) — never shared with the template or another peer — so
        // forceDisposeChildren frees them too. CLAUDE.md: "That same cascade does not free
        // materials" — the default (false) would otherwise leak every one of them, since nothing
        // else in this project's scene graph still references them once this mesh is gone.
        material.dispose(false, false, true);
      } else if (material && material !== this.bodyMaterial) {
        // this.bodyMaterial is skipped here, by identity, not disposed per-mesh: on the
        // primitive-fallback path (no model resolved for this part) it is the very same
        // instance assigned to up to four meshes at once — head, body, both hands — and
        // disposing it once per mesh in this loop used to mean disposing it up to four times.
        // Harmless in Babylon today, but the ownership was muddled in both directions; it is
        // disposed exactly once below instead, regardless of how many — if any — meshes
        // actually reference it.
        material.dispose();
      }

      mesh?.dispose();
    }

    // The other half of the fix this.bodyMaterial's own field doc describes: on the
    // model-backed path (every shipped avatar part today) this instance is never attached to
    // any mesh at all, so the loop above never reaches it — a leaked StandardMaterial per
    // peer, one whole session for however many peers ever joined. Disposed here,
    // unconditionally, exactly once, regardless of which path (model or primitive) any given
    // part actually took.
    this.bodyMaterial.dispose();

    this.root.dispose();
  }
}

// spec: SPEC-108
export class AvatarManager {
  private readonly avatars = new Map<string, Avatar>();
  private readonly models: ModelLibrary;

  /**
   * The last `ToolSwitched` state applied for each peerId — plan/remote-tool-visibility.md.
   * Deliberately **not** cleared by `remove()`/`clear()`: a resync (`main.ts`'s own
   * `hub.onResynced`) calls `clear()` then re-`add()`s every peer from scratch, and the server
   * replays every peer's retained mode again on that same `JoinRoom` call — but the replay can
   * (and, per the "one code path, robust to either order" design, is expected to) arrive
   * *before* `join()`'s own promise resolves and `onResynced` gets to run `clear()`/`add()` at
   * all. Without this map surviving that cycle, a resync would silently forget every peer's own
   * tool state the instant it rebuilds their Avatar. The same map also answers the other half of
   * the same ordering hazard: a switch naming a peer this manager has not `add()`ed yet is held
   * here, harmlessly, and applied the moment that peer's avatar is actually built. Bounded by
   * how many distinct peerIds this browser tab ever sees (a fresh Guid per join —
   * `RoomPresenceRegistry.Join`), the same small, deliberately-not-cleaned-up growth
   * `warnedMissingTintKeys` already accepts for the identical reason.
   */
  private readonly toolStateByPeer = new Map<string, { mode: ToolModeValue; hand: HandValue }>();

  // Defaulted rather than always constructed here so a test can supply one it has already
  // populated via ModelLibrary.loadFromBytes — see AvatarManager.test.ts, and
  // NodeMeshFactory.ts's own constructor for the same shape applied to catalogue nodes. Every
  // real caller (just main.ts) takes the default.
  constructor(
    private readonly scene: Scene,
    models: ModelLibrary = new ModelLibrary(scene),
  ) {
    this.models = models;
  }

  /**
   * Preloads avatar-part models ahead of the first avatar being built — the same "before first
   * use, not on demand" rule ModelLibrary.preloadAll already follows for the node catalogue
   * (CLAUDE.md, "The splash screen exists so entering VR is instant"): `Avatar`'s own
   * construction is synchronous, exactly like `NodeMeshFactory.create`, so whatever has not
   * resolved by the time a peer joins falls back to that part's primitive for the rest of the
   * session. Call once at boot, before the first `add()` — main.ts does, alongside
   * `view.preloadModels(nodeTypes)`.
   *
   * `head.glb`/`body.glb`/`hand.glb` now ship in `public/models/avatars/` — this used to 404 for
   * every key (see this file's own git history), and every fallback path below still exists and
   * is still exercised for a client running against a build that predates them, or a `.glb` that
   * fails to parse. `Avatar` already degrades to its primitive whenever `templateFor(...)` comes
   * back empty, so nothing further is needed here to stay safe.
   *
   * Every call passes `{ normalize: false, preserveMaterials: true }` — unlike the node
   * catalogue's own `preloadAll`, which normalises every type to TARGET_SIZE and discards its
   * imported material by omitting both options entirely. AVATAR_MODEL_KEYS' own doc has the
   * `normalize: false` reasoning; `preserveMaterials: true` is `LoadModelOptions`' own doc's —
   * the short version is that each of these three models is authored with two material slots,
   * `Mat.Avatar.Body` and `Mat.Avatar.Tint`, and without this option `Mesh.MergeMeshes` collapses
   * both into one at load time, which is the "avatar renders as one flat colour" bug this option
   * exists to fix (`applyAvatarMaterial`, this file, is the other half).
   */
  async preloadModels(): Promise<void> {
    const avatarKeys = Object.values(AVATAR_MODEL_KEYS);
    const toolKeys = Object.keys(TOOL_MODEL_PATHS);
    const options = { normalize: false, preserveMaterials: true };

    const outcomes = await Promise.all([
      ...avatarKeys.map((key) =>
        this.models.loadFromPath(key, `${AVATAR_MODEL_PATH_BASE}${key}.glb`, options),
      ),
      // The same three prop models ToolPropRenderer.preloadModels() loads for the local prop —
      // a remote peer's tool prop clones the identical .glb, into this class's own separate
      // ModelLibrary instance (AvatarManager and ToolPropRenderer each construct their own, so
      // there is no key collision to worry about between the two, and no shared cache either).
      ...toolKeys.map((key) => this.models.loadFromPath(key, TOOL_MODEL_PATHS[key]!, options)),
    ]);

    const loaded = outcomes.filter(Boolean).length;
    const total = avatarKeys.length + toolKeys.length;

    console.info(
      `Avatar model preload: ${loaded}/${total} avatar/tool part(s) resolved to a .glb model; ` +
        'the rest render their built-in primitive.',
    );
  }

  add(presence: PresenceDto): void {
    if (this.avatars.has(presence.peerId)) {
      return;
    }

    const avatar = new Avatar(this.scene, presence, this.models);
    this.avatars.set(presence.peerId, avatar);

    // Catches this avatar up on whatever mode this peer already had before its own avatar
    // existed — either a switch that raced ahead of this add() (a fresh join: the server's own
    // JoinRoom replay is not guaranteed to arrive after the peer list that makes it meaningful —
    // plan/remote-tool-visibility.md), or state surviving a resync's clear()+add() cycle (see
    // toolStateByPeer's own doc). A no-op, correctly, for a peer who has never switched away
    // from Build — there is nothing to apply, and none is needed.
    const toolState = this.toolStateByPeer.get(presence.peerId);
    if (toolState) {
      avatar.setTool(toolState.mode, toolState.hand, this.models);
    }
  }

  remove(peerId: string): void {
    this.avatars.get(peerId)?.dispose();
    this.avatars.delete(peerId);
    // toolStateByPeer is deliberately NOT cleared here — see its own doc for why: a resync's
    // clear()+add() cycle calls this exact method per peer, and their retained tool state has to
    // survive it so add() can re-apply it immediately, with no fresh ToolSwitched required.
  }

  clear(): void {
    for (const peerId of [...this.avatars.keys()]) {
      this.remove(peerId);
    }
  }

  /**
   * A peer's microphone just went live — the join this whole feature is: `PeerVoicePlayer`
   * already tracks a live stream per `peerId` (`StartVoiceStream`/`EndVoiceStream`), and
   * `AvatarManager` already keys avatars by the same id, so wiring the two together is a lookup,
   * not new plumbing (no new hub method, no new wire field). `main.ts` calls this right after
   * `PeerVoicePlayer.start()`, passing the `<audio>` element it just built. A no-op for a peerId
   * with no tracked avatar — the same tolerance every other streamed update on this class already
   * has for arriving slightly out of order with `add`/`remove`.
   */
  voiceStreamStarted(peerId: string, audio: HTMLAudioElement): void {
    this.avatars.get(peerId)?.startVoice(audio);
  }

  /** The matching close for `voiceStreamStarted` — see `Avatar.endVoice`'s own doc for why
   *  `Avatar.dispose` also reaches this, for the disconnect-mid-stream case this alone cannot
   *  cover. */
  voiceStreamEnded(peerId: string): void {
    this.avatars.get(peerId)?.endVoice();
  }

  /**
   * A peer's mode just changed — swaps their dominant hand's rendered mesh for whichever prop
   * that mode holds (plan/remote-tool-visibility.md). `main.ts` calls this from its
   * `hub.on('toolSwitched', ...)` registration, for both an ordinary broadcast and the server's
   * own join-time replay to a joiner (`IRoomHubClient.ToolSwitched`'s own remarks) — one code
   * path either way, exactly the design's own point.
   *
   * Always updates `toolStateByPeer` first, regardless of whether this peer's avatar currently
   * exists — see that field's own doc for why an event naming an unknown peer has to be *held*,
   * not dropped the way a stale `AvatarPoseUpdate` sample is (`apply`, below): a pose repeats 15-
   * 20 times a second, so dropping one loses nothing; a mode switch is a one-off announcement,
   * so dropping it would leave that peer showing the wrong prop — or none — until they happen to
   * switch again.
   */
  applyToolSwitch(peerId: string, mode: string, hand: string): void {
    const state = { mode: toToolMode(mode), hand: toHand(hand) };

    this.toolStateByPeer.set(peerId, state);
    this.avatars.get(peerId)?.setTool(state.mode, state.hand, this.models);
  }

  /** Applies an incoming pose. Cheap: it only records samples. */
  apply(update: AvatarPoseUpdate): void {
    const peerId = update.peerId;

    if (peerId === undefined) {
      return;
    }

    const avatar = this.avatars.get(peerId);

    if (!avatar) {
      // A pose can arrive before the join event that introduces its sender; it is dropped
      // rather than queued, because another one is 60ms away.
      return;
    }

    avatar.senderClockAtLastPacket = update.t;
    avatar.localClockAtLastPacket = performance.now();

    avatar.headTrack.push(update.t, update.head.p, update.head.r);

    if (update.leftHand) {
      avatar.leftTrack.push(update.t, update.leftHand.p, update.leftHand.r);
    }

    if (update.rightHand) {
      avatar.rightTrack.push(update.t, update.rightHand.p, update.rightHand.r);
    }

    avatar.leftPinch = update.leftPinch;
    avatar.rightPinch = update.rightPinch;
  }

  /** Called every frame. Advances each avatar to its own delayed render time. */
  update(): void {
    const localNow = performance.now();

    for (const avatar of this.avatars.values()) {
      // Advance the sender's clock by however long it has been since their last packet.
      const senderNow =
        avatar.senderClockAtLastPacket + (localNow - avatar.localClockAtLastPacket);

      avatar.headTrack.sample(senderNow);
      avatar.leftTrack.sample(senderNow);
      avatar.rightTrack.sample(senderNow);

      const visible = avatar.headTrack.primed;
      avatar.head.setEnabled(visible);
      avatar.body?.setEnabled(visible);
      avatar.namePlate.setEnabled(visible);

      if (!visible) {
        continue;
      }

      avatar.head.position.copyFrom(avatar.headTrack.position);
      (avatar.head.rotationQuaternion ??= Quaternion.Identity()).copyFrom(avatar.headTrack.rotation);

      // Translation follows the head on all three axes — crouch and the body drops with it —
      // but rotation takes yaw only. See yawOnlyRotation and the module doc above. Absent for a
      // spectator (no body at all — see `Avatar.body`'s own doc), so this is skipped for one the
      // same way the hands below already are.
      if (avatar.body) {
        avatar.body.position.copyFrom(avatar.headTrack.position).addInPlaceFromFloats(0, -BODY_OFFSET_Y, 0);
        (avatar.body.rotationQuaternion ??= Quaternion.Identity()).copyFrom(
          yawOnlyRotation(avatar.headTrack.rotation),
        );
      }

      avatar.namePlate.position.copyFrom(avatar.headTrack.position.add(new Vector3(0, 0.24, 0)));

      if (avatar.leftHand) {
        this.applyHand(
          avatar.leftHand,
          avatar.toolHand === Hands.Left ? avatar.toolMesh : undefined,
          avatar.leftTrack,
          avatar.leftPinch,
          senderNow,
        );
      }

      if (avatar.rightHand) {
        this.applyHand(
          avatar.rightHand,
          avatar.toolHand === Hands.Right ? avatar.toolMesh : undefined,
          avatar.rightTrack,
          avatar.rightPinch,
          senderNow,
        );
      }
    }
  }

  /**
   * Positions whichever mesh represents this hand this frame — the ordinary hand mesh, or its
   * tool prop's stand-in, chosen by the caller and passed in as `toolMesh` (plan/remote-tool-
   * visibility.md). Both track the *same* pose: a remote peer's prop is not aimed or
   * contact-pointed the way the local first-person one is (`ToolPropRenderer.positionProp`'s own
   * working-point math) — it simply occupies the identical tracked wrist slot the ordinary hand
   * mesh already renders at, because from across a room "something is in that hand, in the right
   * place" is the whole of what this needs to convey.
   */
  private applyHand(
    handMesh: Mesh,
    toolMesh: Mesh | undefined,
    track: PoseTrack,
    pinching: boolean,
    senderNow: number,
  ): void {
    // Hands are nullable on the wire and stop updating when tracking is lost, so a hand that
    // has gone quiet is hidden rather than left hanging in the air — and so is its tool prop,
    // for the identical reason: a marker frozen in mid-air reads as broken exactly like a hand
    // would.
    const live = track.primed && track.stalenessAt(senderNow) < HAND_STALE_MS;
    const showTool = live && toolMesh !== undefined;

    handMesh.setEnabled(live && !showTool);
    toolMesh?.setEnabled(showTool);

    if (!live) {
      return;
    }

    const target = showTool ? toolMesh! : handMesh;

    target.position.copyFrom(track.position);
    (target.rotationQuaternion ??= Quaternion.Identity()).copyFrom(track.rotation);

    // Squeezes when pinching, which is what makes a remote grab legible as a grab — applies to
    // the tool prop too, for whatever a peer's off hand or a mid-mode-switch dominant grip is
    // doing; nothing about Build-only pinch feedback changes here.
    target.scaling.setAll(pinching ? 0.7 : 1);
  }

  start(): () => void {
    const observer = this.scene.onBeforeRenderObservable.add(() => this.update());

    return () => this.scene.onBeforeRenderObservable.remove(observer);
  }

  dispose(): void {
    this.clear();
  }
}

/**
 * Isolates a quaternion's rotation about the world Y axis, discarding pitch and roll.
 *
 * Drives the floating body's rotation from the head's own tracked pose: inheriting pitch or
 * roll would tumble the torso forward every time someone looked down at the floor, so only yaw
 * survives the trip. `toEulerAngles().y` is the same extraction `XrManager`'s recentre already
 * uses to isolate the XR camera's own yaw, and it carries the same trade-off: looking straight
 * up or down (pitch at +/-90 degrees) puts yaw and roll into gimbal lock, where the extracted
 * yaw can jump between samples. That is rare from a head at a desk or standing in a room, and a
 * wrong yaw here only spins a floating decorative body rather than breaking movement or
 * aim the way it would in XrManager, so it is left as a known, cosmetic edge case rather than
 * solved with a second construction.
 *
 * Exported for direct testing — see AvatarManager.test.ts. `FromEulerAngles(0, y, 0)` and
 * `RotationYawPitchRoll(y, 0, 0)` are the same call (`FromEulerAngles(x, y, z)` forwards to
 * `RotationYawPitchRollToRef(y, x, z, ...)`), so this is unambiguously "rotate about Y by the
 * head's own yaw and nothing else," independent of how `toEulerAngles` internally composes the
 * three angles it decomposes a quaternion into.
 */
export function yawOnlyRotation(source: Quaternion): Quaternion {
  return Quaternion.FromEulerAngles(0, source.toEulerAngles().y, 0);
}
